import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { UserProfile } from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { ApiClientError } from '../../lib/api';
import {
  consumeGoogleRedirectAuth,
  getGoogleEmailFromIdToken,
  stashPendingGoogleRegistration,
} from '../../lib/googleIdentity';
import { getRedirectAfterAuth } from '../../lib/authRedirect';
import { AppButton } from '../../components/ui/AppButton';
import { tryCompletePairInviteAfterAuth } from '../../lib/completePendingPairInvite';
import { readPendingPairInvite, writePendingPairInvite } from '../../lib/pairInviteSession';
import { PLAY_BANNER_OFFSET_PADDING_CLASS } from '../../lib/layoutChrome';

type DecisionState =
  | {
      kind: 'notRegistered';
      idToken: string;
      email: string;
      fromPath?: string;
      pairInviteMode?: boolean;
      pairingCode?: string;
    }
  | {
      kind: 'existingGoogle';
      nextUser: UserProfile;
      fromPath?: string;
    }
  | {
      kind: 'emailExists';
    };

export const GoogleCallbackPage = () => {
  const { t } = useLanguage();
  const { loginWithGoogle, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [decision, setDecision] = useState<DecisionState | null>(null);

  const handlePostAuth = useCallback(
    async (nextUser: UserProfile, pairInviteMode?: boolean, fromPath?: string) => {
      if (pairInviteMode || readPendingPairInvite()) {
        const result = await tryCompletePairInviteAfterAuth(nextUser, refreshUser, navigate);
        if (result === 'joined') {
          return;
        }
      }
      navigate(getRedirectAfterAuth(nextUser, fromPath), { replace: true });
    },
    [navigate, refreshUser]
  );

  useEffect(() => {
    const run = async () => {
      try {
        const { idToken, pending } = consumeGoogleRedirectAuth();
        const googleEmail = getGoogleEmailFromIdToken(idToken);

        if (pending.pairInviteMode && pending.pairingCode) {
          const stored = readPendingPairInvite();
          if (!stored) {
            writePendingPairInvite({
              code: pending.pairingCode,
              employerNickname: '',
            });
          }
        }

        let nextUser: UserProfile;
        try {
          nextUser = await loginWithGoogle(idToken);
        } catch (loginError) {
          if (
            loginError instanceof ApiClientError &&
            loginError.code === 'GOOGLE_ACCOUNT_NOT_REGISTERED'
          ) {
            if (pending.intent === 'register' || pending.pairInviteMode) {
              stashPendingGoogleRegistration({
                idToken,
                email: googleEmail,
                fromPath: pending.fromPath,
                pairInviteMode: pending.pairInviteMode,
                pairingCode: pending.pairingCode,
              });
              navigate('/register?googleRegister=1', { replace: true });
              return;
            }
            setDecision({
              kind: 'notRegistered',
              idToken,
              email: googleEmail,
              fromPath: pending.fromPath,
              pairInviteMode: pending.pairInviteMode,
              pairingCode: pending.pairingCode,
            });
            return;
          }
          if (loginError instanceof ApiClientError && loginError.code === 'PASSWORD_LOGIN_REQUIRED') {
            setDecision({ kind: 'emailExists' });
            return;
          }
          throw loginError;
        }
        if (pending.intent === 'register') {
          setDecision({
            kind: 'existingGoogle',
            nextUser,
            fromPath: pending.fromPath,
          });
          return;
        }
        await handlePostAuth(nextUser, pending.pairInviteMode, pending.fromPath);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
      }
    };
    void run();
  }, [handlePostAuth, loginWithGoogle, navigate, t]);

  const handleDecisionConfirm = () => {
    if (!decision) {
      return;
    }
    if (decision.kind === 'notRegistered') {
      stashPendingGoogleRegistration({
        idToken: decision.idToken,
        email: decision.email,
        fromPath: decision.fromPath,
        pairInviteMode: decision.pairInviteMode,
        pairingCode: decision.pairingCode,
      });
      navigate('/register?googleRegister=1', { replace: true });
      return;
    }
    if (decision.kind === 'existingGoogle') {
      navigate(getRedirectAfterAuth(decision.nextUser), { replace: true });
      return;
    }
    navigate('/login', { replace: true });
  };

  const handleDecisionCancel = async () => {
    if (decision?.kind === 'existingGoogle') {
      await logout();
    }
    navigate('/', { replace: true });
  };

  return (
    <div className={`flex min-h-screen items-center justify-center bg-gray-50 px-4 ${PLAY_BANNER_OFFSET_PADDING_CLASS}`}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-sm">
        <h1 className="text-xl font-bold text-gray-900">{t('auth.googleCallbackTitle')}</h1>
        {!error && !decision ? (
          <p className="mt-2 text-sm text-gray-600">{t('auth.googleCallbackLoading')}</p>
        ) : null}
        {!error && decision ? (
          <>
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {decision.kind === 'notRegistered'
                ? t('auth.googleDecisionNotRegistered')
                : decision.kind === 'existingGoogle'
                  ? t('auth.googleDecisionRegisteredGoogle')
                  : t('auth.googleDecisionRegisteredEmail')}
            </p>
            <div className="mt-4 flex gap-2">
              <AppButton
                type="button"
                variant="primary"
                onClick={handleDecisionConfirm}
                className="flex-1"
              >
                {decision.kind === 'notRegistered'
                  ? t('auth.googleDecisionConfirmRegister')
                  : decision.kind === 'existingGoogle'
                    ? t('auth.googleDecisionConfirmLogin')
                    : t('auth.googleDecisionConfirmEmailLogin')}
              </AppButton>
              <AppButton
                type="button"
                variant="secondary"
                onClick={() => void handleDecisionCancel()}
                className="flex-1"
              >
                {t('auth.googleDecisionCancelHome')}
              </AppButton>
            </div>
          </>
        ) : null}
        {error ? (
          <>
            <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
            <Link
              className="mt-4 inline-block text-sm font-medium text-primary-600 underline"
              to="/"
            >
              {t('auth.goToHome')}
            </Link>
          </>
        ) : null}
      </div>
    </div>
  );
};
